<template>
  <div>
    <div class="row mb-4">
      <div class="col-12">
        <h2 class="mb-0">
          <i class="bi bi-speedometer2 mr-2"></i>
          TOSF Management Dashboard
        </h2>
        <p class="text-muted">Overview of tuition and other school fees across HEIs</p>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="row mb-4">
      <div class="col-md-3">
        <div class="card bg-primary text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h4 class="mb-0">{{ totalHeis }}</h4>
                <p class="mb-0">Total HEIs</p>
              </div>
              <div class="align-self-center">
                <i class="bi bi-building display-4"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card bg-success text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h4 class="mb-0">{{ totalPrograms }}</h4>
                <p class="mb-0">Total Programs</p>
              </div>
              <div class="align-self-center">
                <i class="bi bi-mortarboard display-4"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card bg-info text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h4 class="mb-0">₱{{ averageTuition.toLocaleString() }}</h4>
                <p class="mb-0">Average Tuition</p>
              </div>
              <div class="align-self-center">
                <i class="bi bi-currency-dollar display-4"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card bg-warning text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h4 class="mb-0">₱{{ averageOtherFees.toLocaleString() }}</h4>
                <p class="mb-0">Average Other Fees</p>
              </div>
              <div class="align-self-center">
                <i class="bi bi-list-check display-4"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- HEI Statistics Table -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              <i class="bi bi-table mr-2"></i>
              HEI Statistics
            </h5>
            <div class="d-flex align-items-center">
              <select v-model="selectedAcademicYear" class="form-control form-control-sm mr-2" style="width: auto;">
                <option value="">All Academic Years</option>
                <option v-for="ay in uniqueAcademicYears" :key="ay" :value="ay">
                  {{ ay }}
                </option>
              </select>
              <button 
                class="btn btn-sm btn-outline-primary"
                @click="refreshData"
                :disabled="loading"
              >
                <i class="bi bi-arrow-clockwise" :class="{ 'spinner-border spinner-border-sm': loading }"></i>
                Refresh
              </button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Loading...</span>
              </div>
              <p class="mt-2 text-muted">Loading HEI data...</p>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead class="thead-light">
                  <tr>
                    <th>HEI Name</th>
                    <th>HEI Code</th>
                    <th>Academic Year</th>
                    <th>Programs</th>
                    <th>Avg Tuition per Semester</th>
                    <th>Avg Other Fees per Semester</th>
                    <th>Total Avg Fees</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="hei in filteredHeis" :key="hei.id">
                    <td class="font-weight-bold">{{ hei.hei_name }}</td>
                    <td>{{ hei.hei_code }}</td>
                    <td>{{ hei.academic_year }}</td>
                    <td>
                      <span class="badge badge-info">{{ hei.programs_count }}</span>
                    </td>
                    <td class="text-right">₱{{ hei.avg_tuition.toLocaleString() }}</td>
                    <td class="text-right">₱{{ hei.avg_other_fees.toLocaleString() }}</td>
                    <td class="text-right font-weight-bold">₱{{ hei.total_avg_fees.toLocaleString() }}</td>
                    <td>
                      <span 
                        class="badge" 
                        :class="{
                          'badge-success': hei.hei_status === 'Active',
                          'badge-warning': hei.hei_status === 'Inactive',
                          'badge-danger': hei.hei_status === 'Suspended'
                        }"
                      >
                        {{ hei.hei_status }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="row mt-4">
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-bar-chart mr-2"></i>
              Average Tuition by HEI
            </h5>
          </div>
          <div class="card-body">
            <canvas ref="tuitionChart" width="400" height="200"></canvas>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-pie-chart mr-2"></i>
              Average Other Fees by HEI
            </h5>
          </div>
          <div class="card-body">
            <canvas ref="otherFeesChart" width="400" height="200"></canvas>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'TosfDashboard',
  data() {
    return {
      heis: [],
      loading: false,
      selectedAcademicYear: '',
      tuitionChart: null,
      otherFeesChart: null
    };
  },
  computed: {
    totalHeis() {
      return this.heis.length;
    },
    totalPrograms() {
      return this.heis.reduce((total, hei) => total + hei.programs_count, 0);
    },
    averageTuition() {
      if (this.heis.length === 0) return 0;
      const total = this.heis.reduce((sum, hei) => sum + hei.avg_tuition, 0);
      return Math.round(total / this.heis.length);
    },
    averageOtherFees() {
      if (this.heis.length === 0) return 0;
      const total = this.heis.reduce((sum, hei) => sum + hei.avg_other_fees, 0);
      return Math.round(total / this.heis.length);
    },
    uniqueAcademicYears() {
      return [...new Set(this.heis.map(hei => hei.academic_year))].sort();
    },
    filteredHeis() {
      if (!this.selectedAcademicYear) return this.heis;
      return this.heis.filter(hei => hei.academic_year === this.selectedAcademicYear);
    }
  },
  mounted() {
    this.loadHeiData();
  },
  methods: {
    async loadHeiData() {
      this.loading = true;
      try {
        const response = await axios.get('api/hei-profiles');
        const rawHeis = response.data;
        
        // Process HEI data to calculate averages
        this.heis = rawHeis.map(hei => {
          const processedHei = {
            ...hei,
            programs_count: 0,
            avg_tuition: 0,
            avg_other_fees: 0,
            total_avg_fees: 0
          };

          if (hei.programs_data && hei.programs_data.length > 0) {
            processedHei.programs_count = hei.programs_data.length;
            
            let totalTuition = 0;
            let totalOtherFees = 0;
            let semesterCount = 0;

            hei.programs_data.forEach(program => {
              if (program.years) {
                program.years.forEach(year => {
                  if (year.first_sem) {
                    totalTuition += year.first_sem.total_tuition || 0;
                    totalOtherFees += year.first_sem.other_fees || 0;
                    semesterCount++;
                  }
                  if (year.second_sem) {
                    totalTuition += year.second_sem.total_tuition || 0;
                    totalOtherFees += year.second_sem.other_fees || 0;
                    semesterCount++;
                  }
                });
              }
            });

            if (semesterCount > 0) {
              processedHei.avg_tuition = Math.round(totalTuition / semesterCount);
              processedHei.avg_other_fees = Math.round(totalOtherFees / semesterCount);
              processedHei.total_avg_fees = processedHei.avg_tuition + processedHei.avg_other_fees;
            }
          }

          return processedHei;
        });

        // Sort by total average fees (descending)
        this.heis.sort((a, b) => b.total_avg_fees - a.total_avg_fees);

        // Initialize charts after data is loaded
        this.$nextTick(() => {
          this.initCharts();
        });

      } catch (error) {
        console.error('Error loading HEI data:', error);
        toastr.error('Failed to load HEI data');
      } finally {
        this.loading = false;
      }
    },

    initCharts() {
      // Simple bar chart for tuition (using basic HTML5 canvas)
      this.createTuitionChart();
      this.createOtherFeesChart();
    },

    createTuitionChart() {
      const canvas = this.$refs.tuitionChart;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const heis = this.filteredHeis.slice(0, 10); // Show top 10 HEIs
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      if (heis.length === 0) {
        ctx.fillStyle = '#666';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('No data available', canvas.width / 2, canvas.height / 2);
        return;
      }

      const maxValue = Math.max(...heis.map(h => h.avg_tuition));
      const barWidth = canvas.width / heis.length * 0.8;
      const barSpacing = canvas.width / heis.length * 0.2;

      heis.forEach((hei, index) => {
        const barHeight = (hei.avg_tuition / maxValue) * (canvas.height - 60);
        const x = index * (barWidth + barSpacing) + barSpacing / 2;
        const y = canvas.height - barHeight - 30;

        // Draw bar
        ctx.fillStyle = '#007bff';
        ctx.fillRect(x, y, barWidth, barHeight);

        // Draw HEI name
        ctx.fillStyle = '#333';
        ctx.font = '10px Arial';
        ctx.textAlign = 'center';
        ctx.save();
        ctx.translate(x + barWidth / 2, canvas.height - 10);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(this.getAbbreviatedHeiName(hei.hei_name), 0, 0);
        ctx.restore();
      });
    },

    createOtherFeesChart() {
      const canvas = this.$refs.otherFeesChart;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const heis = this.filteredHeis.slice(0, 10); // Show top 10 HEIs
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      if (heis.length === 0) {
        ctx.fillStyle = '#666';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('No data available', canvas.width / 2, canvas.height / 2);
        return;
      }

      const maxValue = Math.max(...heis.map(h => h.avg_other_fees));
      const barWidth = canvas.width / heis.length * 0.8;
      const barSpacing = canvas.width / heis.length * 0.2;

      heis.forEach((hei, index) => {
        const barHeight = (hei.avg_other_fees / maxValue) * (canvas.height - 60);
        const x = index * (barWidth + barSpacing) + barSpacing / 2;
        const y = canvas.height - barHeight - 30;

        // Draw bar
        ctx.fillStyle = '#ffc107';
        ctx.fillRect(x, y, barWidth, barHeight);

        // Draw HEI name
        ctx.fillStyle = '#333';
        ctx.font = '10px Arial';
        ctx.textAlign = 'center';
        ctx.save();
        ctx.translate(x + barWidth / 2, canvas.height - 10);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(this.getAbbreviatedHeiName(hei.hei_name), 0, 0);
        ctx.restore();
      });
    },

    refreshData() {
      this.loadHeiData();
    },

    getAbbreviatedHeiName(heiName) {
      if (!heiName) return 'N/A';
      
      // Split the name into words
      const words = heiName.split(' ');
      
      // If it's a single word or very short, return first 8 characters
      if (words.length === 1) {
        return heiName.length > 8 ? heiName.substring(0, 8) + '...' : heiName;
      }
      
      // If multiple words, take first letter of each word (up to 3 words)
      const abbreviation = words.slice(0, 3).map(word => word.charAt(0).toUpperCase()).join('');
      
      // If abbreviation is too long, truncate
      return abbreviation.length > 6 ? abbreviation.substring(0, 6) : abbreviation;
    }
  },
  watch: {
    filteredHeis() {
      this.$nextTick(() => {
        this.initCharts();
      });
    }
  }
};
</script>

<style scoped>
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.table th {
  border-top: none;
  font-weight: 600;
}

.badge {
  font-size: 0.75em;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>
